import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClockCounterClockwise, CaretDown, CaretUp, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const FIELD_LABELS = {
  fuel_type: "Combustível", station_name: "Posto", city: "Cidade", state: "UF",
  liters: "Litros", unit_price: "R$/L", total: "Total", km_at_fueling: "KM painel",
  payment_method: "Forma pagto", paid_by: "Pago por", date: "Data",
  category: "Categoria", description: "Descrição", quantity: "Qtd", unit_value: "Valor unit.",
  notes: "Obs.",
};
const label = (k) => FIELD_LABELS[k] || k;

function formatVal(v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-400 italic">vazio</span>;
  if (typeof v === "number") return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 3 });
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  return String(v);
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const ACTION_META = {
  update: { Icon: PencilSimple, cls: "bg-blue-100 text-blue-800 border-blue-300", label: "Editou" },
  delete: { Icon: Trash, cls: "bg-red-100 text-red-800 border-red-300", label: "Excluiu" },
  create: { Icon: Plus, cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Criou" },
};

export function AuditLogCard({ entity, title = "Histórico de alterações", limit = 30 }) {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [openIds, setOpenIds] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/audit-log?entity=${entity}&limit=${limit}`);
      setItems(data || []);
    } catch {}
  };
  useEffect(() => { load(); }, [entity]);
  useEffect(() => {
    const h = (e) => { if (!e.detail?.entity || e.detail.entity === entity) load(); };
    window.addEventListener("audit-log-refresh", h);
    return () => window.removeEventListener("audit-log-refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const shown = expanded ? items : items.slice(0, 5);
  if (items.length === 0) return null;

  return (
    <Card data-testid={`audit-log-${entity}`} className="border-slate-200 bg-white">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2 text-slate-700">
          <ClockCounterClockwise size={20} weight="duotone" className="text-slate-500" />
          {title} ({items.length})
        </CardTitle>
        {items.length > 5 && (
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} data-testid={`audit-toggle-${entity}`} className="h-7 gap-1 text-xs">
            {expanded ? <>Mostrar menos <CaretUp size={12} /></> : <>Ver todos <CaretDown size={12} /></>}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {shown.map((it) => {
          const meta = ACTION_META[it.action] || ACTION_META.update;
          const Icon = meta.Icon;
          const isOpen = openIds[it.id];
          const hasDetails = (it.changes && it.changes.length > 0) || (it.action === "delete" && it.before);
          return (
            <div key={it.id} className="border rounded-md p-2 bg-slate-50/50">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold flex items-center gap-1 ${meta.cls}`}>
                  <Icon size={12} weight="fill" /> {meta.label}
                </span>
                <span className="font-semibold text-slate-800 flex-1 min-w-[120px]">{it.summary}</span>
                <span className="text-xs text-slate-500">{it.user_name} · {it.user_role}</span>
                <span className="text-xs text-slate-400 font-mono">{fmtDateTime(it.created_at)}</span>
                {hasDetails && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setOpenIds((s) => ({ ...s, [it.id]: !s[it.id] }))} data-testid={`audit-details-${it.id}`}>
                    {isOpen ? "Ocultar" : "Detalhes"} {isOpen ? <CaretUp size={10} /> : <CaretDown size={10} />}
                  </Button>
                )}
              </div>
              {isOpen && it.action === "update" && it.changes?.length > 0 && (
                <div className="mt-2 border-t pt-2 text-xs space-y-1">
                  {it.changes.map((c, i) => (
                    <div key={i} className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-semibold text-slate-700 w-28">{label(c.field)}:</span>
                      <span className="text-red-700 line-through">{formatVal(c.before)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-emerald-700 font-semibold">{formatVal(c.after)}</span>
                    </div>
                  ))}
                </div>
              )}
              {isOpen && it.action === "delete" && it.before && (
                <div className="mt-2 border-t pt-2 text-xs space-y-1 text-slate-600">
                  <div className="italic text-slate-500 mb-1">Dados excluídos:</div>
                  {Object.entries(it.before)
                    .filter(([k, v]) => v !== null && v !== "" && v !== 0 && !["_id","id","company_id","created_by","created_at","updated_at"].includes(k))
                    .slice(0, 8)
                    .map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="font-semibold w-28">{label(k)}:</span>
                        <span>{formatVal(v)}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
